import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBursaAPIEndPoints, defaultValue } from 'app/shared/model/bursa-api-end-points.model';

const initialState: EntityState<IBursaAPIEndPoints> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/bursa-api-end-points';

// Actions

export const getEntities = createAsyncThunk('bursaAPIEndPoints/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBursaAPIEndPoints[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'bursaAPIEndPoints/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBursaAPIEndPoints>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bursaAPIEndPoints/create_entity',
  async (entity: IBursaAPIEndPoints, thunkAPI) => {
    const result = await axios.post<IBursaAPIEndPoints>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bursaAPIEndPoints/update_entity',
  async (entity: IBursaAPIEndPoints, thunkAPI) => {
    const result = await axios.put<IBursaAPIEndPoints>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bursaAPIEndPoints/partial_update_entity',
  async (entity: IBursaAPIEndPoints, thunkAPI) => {
    const result = await axios.patch<IBursaAPIEndPoints>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bursaAPIEndPoints/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBursaAPIEndPoints>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

interface IAuthParamsCheckCredentials {
  username: string;
  password: string;
}

export const checkCredentials = createAsyncThunk(
  'authentication/check_credentials',
  async (authParams: IAuthParamsCheckCredentials, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/check-credentials', {
        username: authParams.username,
        password: authParams.password,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
  {
    serializeError: serializeAxiosError,
  }
);
// slice

export const BursaAPIEndPointsSlice = createEntitySlice({
  name: 'bursaAPIEndPoints',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(checkCredentials.fulfilled, (state, action) => {
        state.updateSuccess = action.payload;
        state.loading = false;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BursaAPIEndPointsSlice.actions;

// Reducer
export default BursaAPIEndPointsSlice.reducer;
